import React from 'react';

/**
 * Third-party brand marks — Google, LinkedIn, WhatsApp, X.
 *
 * lucide-react is the app's icon set (Phase 6.4), and it deliberately carries no
 * brand logos: a brand mark is not an icon, it is somebody else's trademark with
 * a fixed shape and a fixed colour, and it cannot be restyled the way an outline
 * icon can. `react-icons` was in the dependency list for these four alone, so
 * they live here instead and the dependency is gone.
 *
 * This is the same reasoning as `--ext-*` in TOKEN_CONTRACT §8: a genuinely
 * external colour does not disappear in the migration, it gets a name and an
 * allowlist entry. These are the shape half of that rule.
 *
 * All four take the lucide props signature — `size`, `className`, `color` —
 * so a call site swapping between them and a lucide icon needs no other change.
 * Google and WhatsApp keep their official colours (`fill` is hardcoded per path
 * and ignores `color`); LinkedIn and X are single-path marks that follow
 * `currentColor`, which is how both brands publish their monochrome variant.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  xmlns: 'http://www.w3.org/2000/svg',
  focusable: 'false',
  'aria-hidden': true,
});

export const GoogleIcon = ({ size = '1em', className = '', ...rest }) => (
  <svg {...base(size)} className={className} {...rest}>
    <path fill="#4285F4" d="M23.06 12.25c0-.85-.08-1.67-.22-2.45H12v4.63h6.2a5.3 5.3 0 0 1-2.3 3.48v2.9h3.72c2.18-2 3.44-4.96 3.44-8.56Z" />
    <path fill="#34A853" d="M12 23.5c3.1 0 5.71-1.03 7.62-2.79l-3.72-2.89c-1.03.69-2.35 1.1-3.9 1.1-3 0-5.53-2.02-6.44-4.74H1.72v2.98A11.5 11.5 0 0 0 12 23.5Z" />
    <path fill="#FBBC05" d="M5.56 14.18a6.9 6.9 0 0 1 0-4.36V6.84H1.72a11.5 11.5 0 0 0 0 10.32l3.84-2.98Z" />
    <path fill="#EA4335" d="M12 5.02c1.69 0 3.2.58 4.4 1.72l3.3-3.3C17.7 1.55 15.1.5 12 .5A11.5 11.5 0 0 0 1.72 6.84l3.84 2.98C6.47 7.1 9 5.02 12 5.02Z" />
  </svg>
);

export const WhatsAppIcon = ({ size = '1em', className = '', color = '#25D366', ...rest }) => (
  <svg {...base(size)} className={className} {...rest}>
    <path
      fill={color}
      d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.48 1.34 5L2 22l5.16-1.35a9.94 9.94 0 0 0 4.88 1.27h.01c5.5 0 9.96-4.46 9.96-9.96S17.54 2 12.04 2Zm0 18.18h-.01a8.26 8.26 0 0 1-4.21-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.24 8.24 0 0 1-1.27-4.4c0-4.56 3.72-8.27 8.29-8.27a8.27 8.27 0 0 1 0 16.53Zm4.54-6.19c-.25-.13-1.47-.72-1.7-.8-.23-.09-.39-.13-.56.12-.16.25-.64.8-.78.97-.15.16-.29.18-.53.06-.25-.12-1.05-.39-2-1.23a7.5 7.5 0 0 1-1.38-1.72c-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.44.13-.15.17-.25.25-.41.09-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.43h-.47c-.16 0-.43.06-.65.31-.23.25-.85.83-.85 2.03 0 1.2.87 2.36.99 2.52.12.16 1.72 2.62 4.16 3.67.58.25 1.04.4 1.39.51.59.19 1.12.16 1.54.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z"
    />
  </svg>
);

export const LinkedInIcon = ({ size = '1em', className = '', ...rest }) => (
  <svg {...base(size)} className={className} {...rest}>
    <path
      fill="currentColor"
      d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14Zm1.78 13.02H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0Z"
    />
  </svg>
);

export const XIcon = ({ size = '1em', className = '', ...rest }) => (
  <svg {...base(size)} className={className} {...rest}>
    <path
      fill="currentColor"
      d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.21-6.82-5.97 6.82H1.68l7.73-8.84L1.25 2.25h6.82l4.71 6.23 5.46-6.23Zm-1.16 17.52h1.83L7.01 4.13H5.04l12.04 15.64Z"
    />
  </svg>
);

export default { GoogleIcon, WhatsAppIcon, LinkedInIcon, XIcon };
